import React from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { 
  Calendar, 
  ShieldCheck, 
  Award, 
  Zap, 
  HeartPulse, 
  PhoneCall, 
  MapPin, 
  ArrowRight,
  CheckCircle2,
  Clock
} from "lucide-react";


const ServiceLayout = ({ title, subtitle, heroImage, images = [], children }) => {
    const navigate = useNavigate();
  return (
    <div className="bg-slate-50 min-h-screen font-sans text-slate-900 selection:bg-blue-100">
      
      {/* 1. PREMIUM HERO SECTION */}
      <section className="relative h-[450px] md:h-[550px] flex items-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0 bg-fixed"
          style={{
            backgroundImage: `url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Navy to Transparent Gradient */}
        <div className="absolute inset-0 z-10 bg-gradient-to-r from-blue-950 via-blue-900/80 to-transparent"></div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 w-full text-white">
          <nav className="flex items-center space-x-3 text-xs md:text-sm mb-8 opacity-80 font-semibold tracking-widest uppercase">
            <Link to="/" className="hover:text-blue-300 transition-colors">Home</Link>
            <span className="text-blue-400">/</span>
            <Link to="/services" className="hover:text-blue-300 transition-colors">Services</Link>
            <span className="text-blue-400">/</span>
            <span className="text-blue-200">{title}</span>
          </nav>

          <h1 className="text-4xl md:text-7xl font-bold mb-6 leading-[1.1] max-w-4xl">
            {title}
          </h1>

          <div className="max-w-2xl border-l-4 border-blue-500 pl-8 py-2">
            <p className="text-lg md:text-2xl opacity-90 leading-relaxed font-light">
              {subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* 2. MAIN LAYOUT GRID */}
      <main className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 xl:gap-16">
          
          {/* CONTENT AREA (Left) */}
          <div className="lg:col-span-8">
            
            {/* FEATURED 2-IMAGE GRID (START OF CONTENT) */}
            {images.length >= 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                <div className="group overflow-hidden rounded-[2rem] shadow-2xl border-4 border-white mt-0 md:mt-8">
                  <img 
                    src={images[0]} 
                    alt="Clinical Setup 1" 
                    className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="group overflow-hidden rounded-[2rem] shadow-2xl border-4 border-white mt-0 md:mt-8">
                  <img 
                    src={images[1]} 
                    alt="Clinical Setup 2" 
                    className="w-full h-64 md:h-80 object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            )}

            {/* DYNAMIC CONTENT */}
            <article className="prose prose-lg prose-slate max-w-none 
              prose-headings:text-blue-950 prose-headings:font-black 
              prose-p:text-slate-600 prose-p:leading-relaxed 
              prose-strong:text-blue-900 prose-li:marker:text-blue-500">
              <div className="service-main-text space-y-12">
                {children}
              </div>
            </article>
          </div>

          {/* SIDEBAR (Right) */}
          <aside className="lg:col-span-4">
            <div className="sticky top-28 space-y-8">
              
              {/* Appointment Card */}
              <div className="bg-white rounded-[2.5rem] p-10 shadow-2xl shadow-blue-900/5 border border-slate-100">
                <div className="bg-blue-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-8 shadow-xl shadow-blue-200">
                  <Calendar className="text-white w-8 h-8" />
                </div>
                <h3 className="text-2xl font-black text-blue-950 mb-3 tracking-tight">Schedule Your Visit</h3>
                <p className="text-slate-500 mb-8 text-sm leading-relaxed">Book a consultation at our world-class facilities in Vijayawada or Hyderabad.</p>
                
                <div className="space-y-4 mb-10">
                  {["Expert Surgeons", "Painless Tech", "Digital Scans"].map((feat) => (
                    <div key={feat} className="flex items-center gap-3 text-sm font-bold text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-blue-500" />
                      {feat}
                    </div>
                  ))}
                </div>

                <button 
                onClick={() => navigate("/book-appointment")}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-5 rounded-2xl transition-all shadow-xl hover:-translate-y-1 flex items-center justify-center gap-3">
                  Book Appointment <ArrowRight className="w-5 h-5" />
                </button>
              </div>

              {/* Location Info */}
              <div className="bg-blue-950 rounded-[2.5rem] p-10 text-white shadow-2xl">
                <h4 className="text-lg font-bold mb-8 flex items-center gap-3 text-blue-300">
                  <MapPin className="w-6 h-6" /> Our Locations
                </h4>
                <div className="space-y-8">
                  <div>
                    <p className="text-blue-400 font-black text-xs uppercase tracking-[0.2em] mb-2">Vijayawada</p>
                    <p className="text-base font-medium opacity-90">Eluru Road Branch</p>
                  </div>
                  <div>
                    <p className="text-blue-400 font-black text-xs uppercase tracking-[0.2em] mb-2">Hyderabad</p>
                    <p className="text-base font-medium opacity-90">KPHB & Nallagandla</p>
                  </div>
                  <div className="pt-6 border-t border-white/10 flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-400" />
                    <p className="text-xs opacity-60 font-semibold uppercase tracking-widest">Mon-Sat: 10AM - 8PM</p>
                  </div>
                </div>
              </div>

              {/* Emergency CTA */}
              <div className="bg-blue-50 rounded-[2rem] p-6 flex items-center gap-4 border border-blue-100">
                <div className="bg-blue-600 p-3 rounded-full text-white">
                    <PhoneCall className="w-5 h-5" />
                </div>
                <div>
                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Quick Contact</p>
                    <a
    href="tel:+919490948081"
    className="text-lg font-black text-blue-950 hover:underline"
  >
    +91 9490948081
  </a>
                </div>
              </div>

            </div>
          </aside>
        </div>
      </main>

      {/* 3. TRUST SIGNALS SECTION */}
      <section className="bg-white py-24 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-blue-950 mb-6">Precision Meets Care</h2>
            <div className="w-24 h-2 bg-blue-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { icon: <Award />, title: "Specialist MDS", desc: "Consult with internationally certified dental surgeons." },
              { icon: <Zap />, title: "CAD/CAM Ready", desc: "Same-day restorations with advanced digital milling." },
              { icon: <ShieldCheck />, title: "AERB Certified", desc: "Safe imaging protocols ensuring minimal radiation." },
              { icon: <HeartPulse />, title: "Holistic Care", desc: "Personalized dental journeys for patients of all ages." }
            ].map((item, i) => (
              <div key={i} className="group p-10 rounded-[3rem] bg-slate-50 hover:bg-white hover:shadow-2xl hover:shadow-blue-900/5 transition-all duration-500 border border-transparent hover:border-blue-100 text-center">
                <div className="text-blue-600 mb-8 flex justify-center transform group-hover:scale-110 transition-transform">
                  {React.cloneElement(item.icon, { size: 48, strokeWidth: 1.5 })}
                </div>
                <h4 className="font-black text-xl text-blue-950 mb-4">{item.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. FINAL CTA */}
      <section className="bg-blue-950 py-24">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-6xl font-bold text-white mb-10 leading-tight">
            Take the first step toward <br className="hidden md:block"/> your <span className="text-blue-500">New Smile</span> today.
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <button 
            onClick={() => navigate("/book-appointment")}
            className="bg-blue-600 hover:bg-blue-500 text-white px-12 py-5 rounded-full font-black text-lg shadow-2xl transition-all hover:-translate-y-1">
              Book Appointment
            </button>
            <button
            onClick={() => navigate("/contact")} 
            className="border-2 border-white/20 text-white px-12 py-5 rounded-full font-black text-lg hover:bg-white hover:text-blue-950 transition-all">
              Contact Us
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ServiceLayout;